// @flow

import React, { useEffect, useState } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { compose } from "redux";

import type { State } from "@store/reducers";
import {
  Loader,
  Text,
  View,
  Button,
  GSGrid,
  ManagerSmsNotificationsSmsList,
  Icons,
  EmailNotificationAddModal,
  EmailNotificationEditModal,
  EmailNotificationDeleteModal,
  ManagerSmsNotificationEditModal,
  ManagerSmsNotificationDeleteModal,
} from "@components";
import withErrorHandler from "@components/error";

import serviceActionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import type { EmailNotification } from "@modules/service/reducer/email-notifications/types";
import { formatDate } from "@transformers/date";
import { formatAuthoredBy } from "@transformers/names";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { HierarchyAssignment } from "@modules/service/types";

type PropsFromState = $Exact<{
  // Email Absence Notification
  isLoading?: boolean,
  notificationsList: Array<EmailNotification>,
  currentEmailNotification: ?EmailNotification,
  emailNotificationAssignments: Array<HierarchyAssignment>,
  showAddEmailNotificationModal: boolean,
  showEditEmailNotificationModal: boolean,
  showDeleteEmailNotificationModal: boolean,
  defaultStepEmailNotificationModal: number,
}>;

type PropsFromDispatch = $Exact<{
  // Email Absence Notification
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
  getEmailNotificationSchema: typeof serviceActionCreators.GetEmailNotificationSchema,
  getEmailNotifications: typeof serviceActionCreators.GetEmailNotifications,
  getEmailNotificationAssignments: typeof serviceActionCreators.GetEmailNotificationAssignments,
  updateEmailNotification: typeof serviceActionCreators.UpdateEmailNotification,
  setCurrentEmailNotification: typeof serviceActionCreators.SetCurrentEmailNotification,
  setEditedEmailNotification: typeof serviceActionCreators.SetEditedEmailNotification,
  setEmailNotificationAssignments: typeof serviceActionCreators.SetEmailNotificationAssignments,
  setShowAddEmailNotificationModal: typeof serviceActionCreators.SetShowAddEmailNotificationModal,
  setShowEditEmailNotificationModal: typeof serviceActionCreators.SetShowEditEmailNotificationModal,
  setShowDeleteEmailNotificationModal: typeof serviceActionCreators.SetShowDeleteEmailNotificationModal,
}>;

type Props = $Exact<{
  clientId: number | null,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

const EmailSettings = (props: Props) => {
  useEffect(() => {
    props.getEmailNotificationSchema({ clientId: props.clientId });
    props.getHierarchyTree({ clientId: props.clientId });
    props.getEmailNotifications({ clientId: props.clientId });
  }, [props.clientId]);

  const columns = [
    {
      name: props.t("EMAIL_NOTIFICATION_PROFILE_ID"),
      key: "id",
      width: 100,
    },
    {
      name: props.t("EMAIL_NOTIFICATION_PROFILE_NAME"),
      key: "name",
      align: "left",
    },
    {
      key: "hierarchyScope",
      name: props.t("EMAIL_NOTIFICATION_HIIERARCHY_SCOPE"),
      align: "left",
      renderCell: ({ row }: { row: EmailNotification }) =>
        row.hierarchyAssignments && row.hierarchyAssignments.map(({ label }) => label).join(", "),
    },
    {
      name: props.t("EMAIL_NOTIFICATION_DATE_ADDED"),
      key: "dateAdded",
      renderCell: ({ row }: { row: EmailNotification }) => formatDate(row.createdAt),
    },
    {
      name: props.t("EMAIL_NOTIFICATION_ADDED_BY"),
      key: "addedBy",
      align: "left",
      renderCell: ({ row }: { row: EmailNotification }) => formatAuthoredBy(row.createdBy),
    },
    {
      key: "status",
      name: props.t("EMAIL_NOTIFICATION_STATUS"),
      align: "center",
      width: 150,
      onCellClick: row => {
        !row.isDefault && setItemStatus(row, !row.isActive);
      },
      renderCell: ({ row }: { row: EmailNotification }) => (
        <View direction="row" justifyContent="space-between">
          {GSGrid.render.statusPill(props.t(row.isActive ? "ACTIVE" : "INACTIVE"))}
          {!row.isDefault && row.isActive ? (
            <Icons.EnabledIcon className="w-15 pointer" />
          ) : (
            <Icons.DisabledIcon className="w-15 pointer" />
          )}
        </View>
      ),
    },
    {
      name: props.t("EDIT"),
      key: "edit",
      width: 50,
      align: "center",
      onCellClick: row => handleShowEdit(row),
      renderCell: ({ row }: { row: EmailNotification }) => GSGrid.render.materialIcon("edit"),
    },
    {
      name: props.t("DELETE"),
      key: "delete",
      width: 50,
      align: "center",
      onCellClick: row => !row.isDefault && handleShowDelete(row),
      renderCell: ({ row }: { row: EmailNotification }) =>
        !row.isDefault && GSGrid.render.materialIcon("delete"),
    },
  ];

  function setItemStatus(item: EmailNotification, active: boolean) {
    return props.updateEmailNotification({
      clientId: props.clientId,
      rule: { ...item, isActive: active },
      assignments: props.emailNotificationAssignments,
      isAssigmentsRequired: false,
    });
  }

  function handleShowAdd() {
    props.setEditedEmailNotification({});
    props.setEmailNotificationAssignments([]);
    props.setShowAddEmailNotificationModal(true);
  }

  function handleShowEdit(item: EmailNotification) {
    props.setCurrentEmailNotification(item);
    props.setEditedEmailNotification(item);
    props.getEmailNotificationAssignments({
      clientId: props.clientId,
      ruleId: item.id,
    });
    props.setShowEditEmailNotificationModal({ show: true, step: 1 });
  }

  function handleShowDelete(item: EmailNotification) {
    props.setCurrentEmailNotification(item);
    props.setShowDeleteEmailNotificationModal(true);
  }

  return (
    <View gap="6">
      {props.isLoading && <Loader />}
      {/* Email Absence Notification */}
      <View className="gs-container" gap="6">
        <View justifyContent="space-between" direction="row">
          <Text type="h6" text={props.t("EMAIL_NOTIFICATIONS")} />
          <Button
            materialIcon="add_box"
            label={props.t("ADD")}
            onClick={handleShowAdd}
            size="small"
          />
        </View>
        <GSGrid rows={props.notificationsList || []} columns={columns} />
      </View>

      {/* Modals */}
      {props.showAddEmailNotificationModal && (
        <EmailNotificationAddModal
          editing={props.showEditEmailNotificationModal}
          clientId={props.clientId}
        />
      )}
      {props.showEditEmailNotificationModal && !props.isLoading && (
        <EmailNotificationEditModal
          editing={props.showEditEmailNotificationModal}
          clientId={props.clientId}
          defaultStep={props.defaultStepEmailNotificationModal}
        />
      )}
      {props.showDeleteEmailNotificationModal && (
        <EmailNotificationDeleteModal
          clientId={props.clientId}
          data={props.currentEmailNotification}
        />
      )}
      {/* Modals END */}
    </View>
  );
};

const mapStateToProps = (state: State): PropsFromState => {
  return {
    isLoading: serviceSelectors.isLoading(state),
    notificationsList: serviceSelectors.getEmailNotificationsList(state),
    currentEmailNotification: serviceSelectors.getCurrentEmailNotification(state),
    emailNotificationAssignments: serviceSelectors.getEmailNotificationAssignments(state),
    showAddEmailNotificationModal: serviceSelectors.getShowAddEmailNotificationModal(state),
    showEditEmailNotificationModal: serviceSelectors.getShowEditEmailNotificationModal(state),
    showDeleteEmailNotificationModal: serviceSelectors.getShowDeleteEmailNotificationModal(state),
    defaultStepEmailNotificationModal: serviceSelectors.getDefaultStepEmailNotificationModal(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  // Email Absence Notification
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
  getEmailNotificationSchema: serviceActionCreators.GetEmailNotificationSchema,
  getEmailNotifications: serviceActionCreators.GetEmailNotifications,
  getEmailNotificationAssignments: serviceActionCreators.GetEmailNotificationAssignments,
  updateEmailNotification: serviceActionCreators.UpdateEmailNotification,
  setCurrentEmailNotification: serviceActionCreators.SetCurrentEmailNotification,
  setEditedEmailNotification: serviceActionCreators.SetEditedEmailNotification,
  setEmailNotificationAssignments: serviceActionCreators.SetEmailNotificationAssignments,
  setShowAddEmailNotificationModal: serviceActionCreators.SetShowAddEmailNotificationModal,
  setShowEditEmailNotificationModal: serviceActionCreators.SetShowEditEmailNotificationModal,
  setShowDeleteEmailNotificationModal: serviceActionCreators.SetShowDeleteEmailNotificationModal,
};

export default compose(
  withErrorHandler({}),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmailSettings);
